import { gql } from '@apollo/client';

/**
 * Agent quality — what the agents got wrong, and the prompt changes that fix it.
 *
 * One loop in two halves. Conversations are analysed into ISSUES, each a turn
 * where an agent did the wrong thing; triaging an issue marks it fixed, won't
 * fix, or promotes it to a lesson. Those lessons drive an IMPROVEMENT RUN,
 * which produces a new PROMPT VERSION that can be compared and activated.
 *
 * All of this is on the main schema. app_admin builds a separate Apollo client
 * for `/agents/graphql` to reach it — the unified schema carries every one of
 * these fields, so no second transport is needed here.
 */

/** Issues rolled up per agent and category — where to start looking. */
export const CONVERSATION_ISSUE_SUMMARY = gql`
  query ConversationIssueSummary(
    $agentName: String
    $sinceHours: Int
    $onlyUnreviewed: Boolean!
    $onlyUnpromoted: Boolean!
    $status: String
    $limit: Int!
  ) {
    conversationIssueSummary(
      agentName: $agentName
      sinceHours: $sinceHours
      onlyUnreviewed: $onlyUnreviewed
      onlyUnpromoted: $onlyUnpromoted
      status: $status
      limit: $limit
    ) {
      agentName
      category
      total
      high
      medium
      low
      unreviewed
      lastSeen
    }
  }
`;

/**
 * The issues themselves.
 *
 * `isApplied` is the one filter worth knowing: false is the backlog of fixes
 * that have been agreed but not yet written into a prompt version.
 */
export const LIST_CONVERSATION_ISSUES = gql`
  query ListConversationIssues(
    $agentName: String
    $category: String
    $minSeverity: String
    $reviewed: Boolean
    $promoted: Boolean
    $status: String
    $isApplied: Boolean
    $sinceDays: Int
    $limit: Int!
  ) {
    listConversationIssues(
      agentName: $agentName
      category: $category
      minSeverity: $minSeverity
      reviewed: $reviewed
      promoted: $promoted
      status: $status
      isApplied: $isApplied
      sinceDays: $sinceDays
      limit: $limit
    ) {
      id
      threadName
      userId
      agentName
      turnIndex
      userMessage
      agentResponse
      category
      severity
      status
      explanation
      suggestedFix
      targetText
      isApplied
      appliedInVersionId
      detector
      reviewed
      reviewerNotes
      reviewerId
      reviewedAt
      promotedToLesson
      createdAt
    }
  }
`;

/** Every agent seen in conversations, with its issue counts. */
export const KNOWN_AGENTS = gql`
  query KnownAgents($sinceHours: Int) {
    knownAgents(sinceHours: $sinceHours) {
      name
      conversationCount
      openIssueCount
      fixedIssueCount
      wontFixIssueCount
      totalIssueCount
    }
  }
`;

/** The agents whose prompts can actually be improved — the enum, as a list. */
export const IMPROVABLE_AGENTS = gql`
  query ImprovableAgents { improvableAgents }
`;

/** Where each agent stands: active version, last run, and the issue rate around it. */
export const SELF_IMPROVEMENT_STATUS = gql`
  query SelfImprovementStatus($agentNames: [String!]) {
    selfImprovementStatus(agentNames: $agentNames) {
      agentName
      activeVersion
      activeSource
      lastRunStatus
      lastRunAt
      issueRateBefore
      issueRateAfter
    }
  }
`;

/**
 * One agent's prompt versions.
 *
 * `issueRateBefore` / `issueRateAfter` are the only evidence a version helped,
 * which is what makes activating one a judgement rather than a formality.
 */
export const PROMPT_VERSIONS = gql`
  query PromptVersions($agentName: String!, $limit: Int!) {
    promptVersions(agentName: $agentName, limit: $limit) {
      id
      agentName
      version
      source
      isActive
      status
      issueRateBefore
      issueRateAfter
      improvementRunId
      activatedBy
      activatedAt
      rejectedReason
      createdAt
    }
  }
`;

/** A version's actual prompt text, read on demand. */
export const PROMPT_VERSION_CONTENT = gql`
  query PromptVersionContent($versionId: Int!) {
    promptVersionContent(versionId: $versionId) {
      id
      agentName
      version
      source
      isActive
      status
      issueRateBefore
      issueRateAfter
      createdAt
      content
    }
  }
`;

/** The improvement runs behind those versions. */
export const IMPROVEMENT_RUNS = gql`
  query ImprovementRuns($agentName: String!, $limit: Int!) {
    improvementRuns(agentName: $agentName, limit: $limit) {
      id
      agentName
      startedAt
      finishedAt
      status
      issuesUsedCount
      rulesAppliedCount
      promptVersionId
      summary
      error
      createdAt
    }
  }
`;
